import {
  AVAILABILITY_DATE_FORMAT,
  ORDER_FULFILLMENT_TYPE_TO_TRANSLATION_KEY_MAP,
} from '@domain/fulfillment-settings/blocs/order-availability';
import {
  AvailabilityDayFragment,
  FulfillmentName,
  FulfillmentTimeslotSelection,
  GetAvailableOrderFulfillmentResponse,
} from '@/types';
import { Moment } from 'moment-timezone';
import { Timeslot } from '@domain/fulfillment-settings/types/types';

/**
 * Check if a fulfillment type exists in the available fulfillments.
 */
export const isFulfillmentTypeAvailable = (
  type: FulfillmentName,
  availableFulfillments: GetAvailableOrderFulfillmentResponse[],
): boolean => {
  return availableFulfillments.some((f) => f.fulfillment_name === type);
};

/**
 * Validate a timeslot against an availability day's settings.
 * Returns the validated timeslot, a default timeslot, or null.
 *
 * Logic matches operator portal's getValidTimeslotForAvailability in use-selected-fulfillment.ts
 */
export const getValidTimeslotForAvailability = (
  availabilityDay: AvailabilityDayFragment | null | undefined,
  timeslot: Timeslot | null,
): Timeslot | null => {
  const timeslotSettings = availabilityDay?.timeslot_settings;

  // no timeslot settings = timeslot should be null
  if (!timeslotSettings) {
    return null;
  }

  // if default is none, null is a valid timeslot
  const defaultNone =
    timeslotSettings.default_selection === FulfillmentTimeslotSelection.None;
  if (!timeslot && defaultNone) {
    return null;
  }

  // check if saved timeslot exists in available timeslots
  const isTimeslotValid =
    timeslot &&
    timeslotSettings.timeslots.some(
      ({ start_time, end_time }) =>
        start_time === timeslot.startTime && end_time === timeslot.endTime,
    );

  if (isTimeslotValid) {
    return timeslot;
  }

  // saved timeslot is invalid - use default selection to either clear it or select first available
  const firstAvailable = timeslotSettings.timeslots[0];
  if (defaultNone || !firstAvailable) {
    return null;
  }

  return {
    endTime: firstAvailable.end_time,
    startTime: firstAvailable.start_time,
  };
};

/**
 * Get valid timeslot for a specific date given the fulfillment availability.
 */
export const getValidTimeslotForDate = (
  availableFulfillment: GetAvailableOrderFulfillmentResponse,
  dateMoment: Moment,
  savedTimeslot: Timeslot | null,
): Timeslot | null => {
  const dateString = dateMoment.format(AVAILABILITY_DATE_FORMAT);
  const availabilityDay = availableFulfillment.availability.find(
    (av) => av.date === dateString,
  );

  if (!availabilityDay) {
    return null;
  }

  return getValidTimeslotForAvailability(availabilityDay, savedTimeslot);
};

/**
 * Get the display name for a fulfillment type.
 * Uses custom fulfillment_display_name if available, falls back to translation map.
 */
export const getFulfillmentDisplayName = (
  fulfillmentType: FulfillmentName | null,
  availableFulfillments: GetAvailableOrderFulfillmentResponse[],
): string => {
  if (!fulfillmentType) {
    return '';
  }
  const fulfillment = availableFulfillments.find(
    (f) => f.fulfillment_name === fulfillmentType,
  );
  return (
    fulfillment?.fulfillment_display_name ||
    ORDER_FULFILLMENT_TYPE_TO_TRANSLATION_KEY_MAP[fulfillmentType]
  );
};
